import * as React from 'react';
import axios, { CancelTokenSource, CancelTokenStatic } from 'axios';
import themePrimary from '../../styles/themePrimary';
import clsx from 'clsx';
import { useHistory } from 'react-router';
import { ErrorCode } from '../../models/common/ErrorCode';
import { FormControl, IconButton, makeStyles, Select, Typography, useMediaQuery } from '@material-ui/core';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutState, CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import { PaymentMethodsAccepted } from '../../models/payment/PaymentMethodsAccepted';
import { ElementDimensions } from '../../styles/ElementDimensions';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import { String_PayCheck_CardExpirationDateFormatError } from '../../utilities/StringHelper';
import { CheckState, CheckStateContext } from '../../providers/CheckStateContextProvider';
import PaymentTerms from './PaymentTerms';
import GooglePayButton from '@google-pay/button-react';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { ApplePaySessionRequest } from '../../models/payment/ApplePaySessionRequest';
import { ApplePaySessionResponse } from '../../models/payment/ApplePaySessionResponse';
import { PaymentDetail } from '../../models/payment/PaymentDetail';
import { MakePaymentRequest } from '../../models/payment/MakePaymentRequest';
import { PaymentDigitalWalletType } from '../../models/payment/PaymentDigitalWalletType';
import { String_Check_CheckPaid, String_PayCheck_ErrorCreditCard, String_PayCheck_ErrorPaymentFailed, String_PayCheck_PaymentExceedsAmountDue, String_System_ConnectionError, String_System_GeneralError } from '../../utilities/StringHelper';
import TextField, { TextFieldChangeHandler, TextFieldLabelPosition, TextFieldProps } from '../common/TextField';
import { MakePaymentResponse } from '../../models/payment/MakePaymentResponse';
import { SplitType } from '../../models/common/SplitType';
import { CreatePaymentRequest } from '../../models/payment/CreatePaymentRequest';
import { CreatePaymentResponse } from '../../models/payment/CreatePaymentResponse';
import { ApplePayHeaderRaw, ApplePayPaymentDataRaw, ApplePayPaymentMethodRaw, ApplePayTokenRaw } from '../../models/payment/ApplePayTokenRaw';
import { ApplePayToken } from '../../models/payment/ApplePayToken';
import { PaymentMethod } from '../../models/payment/PaymentMethod';

const useStyles = makeStyles({
    iframe: {
        width: '90vw',
        height: '100%',
        borderWidth: '0',
    },
    iframeLandscape: {
        width: 'calc(100vw * .7)',
        height: '100%',
        borderWidth: '0',
    },
    fieldContainer: {
        height: '100%',
    },
    fieldLabel: {
        textAlign: 'left',
    },
    creditButton: {
        backgroundColor: '#000000 !important',
        color: themePrimary.palette.primary.contrastText,    
        boxShadow: 'none',
        fontSize: '1.5em',
        width: '300px',
        height: '46px',
        lineHeight: '46px',
        borderRadius: '5px',
        marginTop: '20px',
        marginLeft: 'auto',
        marginRight: 'auto',
        fontFamily: 'LeagueSpartan-Regular !important',
        fontWeight: 'bold',
    },
    errorMessageContainer: {
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: 'center',
        height: '2em'
    },
    errorMessage: {
        fontSize: '1rem',
        color: themePrimary.palette.error.main,
        fontFamily: 'LeagueSpartan-Regular !important',
    },
    googlePayButton: {
        width: '300px',
        height: '46px',
        marginTop: '20px !important',
        marginBottom: '0px !important',
    },
    paymentTerms: {
        marginLeft: ElementDimensions.margin20 + 'px',
        marginRight: ElementDimensions.margin20 + 'px',
        marginBottom: ElementDimensions.margin20 + 'px',
    },
    root: {
        textAlign: 'center',
        height: 'calc(100vh - ' + (73 + ElementDimensions.appBarHeight + ElementDimensions.stepperHeight + ElementDimensions.footerBarHeightPaymentScreen) + 'px)',
    },
    rootLandscape: {
        textAlign: 'center',
        height: '150%',
    },
});

export type ParPayGooglePaySuccessHandler = (paymentRequest: any) => any;
export type ParPaySuccessHandler = (token: string) => any;
export type ParPayValidateHandler = (success: boolean) => any;
export type ParPayErrorHandler = () => any;

export interface ParPayPaymentProps {
    paymentMethodAllowed: PaymentMethodsAccepted | undefined,
    total: number,
    successHandler: ParPaySuccessHandler,
    validateHandler: ParPayValidateHandler,
    errorHandler: ParPayErrorHandler,
    googlePaySuccessHandler: ParPayGooglePaySuccessHandler
}

export default function ParPayPayment(props: ParPayPaymentProps) {
    const { paymentMethodAllowed, total, successHandler, validateHandler, errorHandler, googlePaySuccessHandler } = props;
    const isPortrait = true;
    const applePayEl = React.useRef(null);
    let history = useHistory();
    const [iframeUrl, setIframeUrl] = React.useState('');
    const [isAuthorized, setIsAuthorized] = React.useState<boolean>(false);
    const [acceptedPaymentTypes, setAcceptedPaymentTypes] = React.useState('');
    const [isError, setIsError] = React.useState<boolean>(false);
    const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
    const [isCreditCardSelected, setIsCreditCardSelected] = React.useState<boolean>(false);
    const [applePayLoaded, setApplePayLoaded] = React.useState<boolean>(false);
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [errorDialogTryAgain, setErrorDialogTryAgain] = React.useState<boolean>(true);
    const [checkPaidError, setCheckPaidError] = React.useState<boolean>(false);
    const [paymentExceedsAmountDueError, setPaymentExceedsAmountDueError] = React.useState<boolean>(false);
    const [paymentError, setPaymentError] = React.useState<boolean>(false);
    const [totalChangedError, setTotalChangedError] = React.useState<boolean>(false);
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const checkState = React.useContext(CheckStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    const applePayScriptLoaded = () => {
        //@ts-ignore
        if (window.ApplePaySession) {
            var merchantIdentifier = 'merchant.io.checkplease.parpay';
            //@ts-ignore
            var promise = ApplePaySession.canMakePaymentsWithActiveCard(merchantIdentifier);
            promise.then(function (canMakePayments: boolean) {
                if (canMakePayments) {
                    const applePayDiv: HTMLDivElement = applePayEl.current!!
                    applePayDiv.innerHTML = "<apple-pay-button onclick='var data = {action: \"applePay\"}; window.postMessage(data);' id='applePayButton' buttonstyle='black' type='check-out' style='margin-top: 20px !important; margin-bottom: 0px !important; --apple-pay-button-width: 300px; --apple-pay-button-height: 40px; --apple-pay-button-border-radius: 5px;--apple-pay-button-padding: 5px 0px;'></apple-pay-button>"
                    setApplePayLoaded(true);
                }
        }); }
    }

    const applePayScriptError = () => {
        
    }

    const getApplePayScriptUrl = (): string => {
        return "https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js"
    }

    const onApplePayClickHandler = async () => {
        var request = {
            countryCode: checkOutState.state.restaurantCountry,
            currencyCode: checkOutState.state.restaurantCurrency,
            supportedNetworks: getPaymentMethodArrayApple(),
            merchantCapabilities: ["supportsCredit", "supportsDebit","supports3DS"],
            total: { label: checkOutState.state.restaurantName!!, amount: (total / 100).toString() },
          }
          //@ts-ignore
          var session = new ApplePaySession(1, request);

          session.onvalidatemerchant = function(event: any) {
            const cancelToken = axios.CancelToken;
            const source = cancelToken.source();

            const applePaySessionRequest: ApplePaySessionRequest = {
                user_id: userState.state.userId!!,
                validation_url: event.validationUrl,
            }

            let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/checks/" + checkState.state.checkSummary!!.id + "/payment/applepay"

            const headers = {
                'X-API-Key': checkOutState.state.restaurantApiKey,
                'location_id': checkOutState.state.restaurantId
            }

            axios.post(url, applePaySessionRequest, { headers, cancelToken: source.token })
            .then(function(response) {
                let r = response.data as ApplePaySessionResponse;

                if (r.success === true) {
                    session.completeMerchantValidation(JSON.parse(r.session));
                }
                else {
                    setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogTryAgain(true);
                    setErrorDialogOpen(true);
                }
            })
            .catch(function(error) {
                if (error.response) {
                    setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogTryAgain(true);
                    setErrorDialogOpen(true);
                }
                else {
                    setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogTryAgain(true);
                    setErrorDialogOpen(true);
                }
            });
        }

        session.onpaymentauthorized = function(event: any) {
            const cancelToken = axios.CancelToken;
            const source = cancelToken.source();

            console.log(JSON.stringify(event.payment.token.paymentData));

            const paymentDetail: PaymentDetail = {
                tip: checkState.state.tip,
                tax: checkState.state.checkTotal!!.tax,
                sub_total: checkState.state.checkTotal!!.sub_total,
                amount_to_pay: checkState.state.checkTotal!!.amount_due,
                total: checkState.state.checkTotal!!.total,
                zip_code: '',
                token: '',
                serial_number: '',
                extended_card_data: '',
                card_number_last_four: '',
                guest_last_name: '',
                room_number: '',
                session_id: ''
            }
            
            const applePayToken: ApplePayToken = {
                signature: event.payment.token.paymentData.signature,
                data: event.payment.token.paymentData.data,
                publicKeyHash: event.payment.token.paymentData.header.publicKeyHash,
                transactionId: event.payment.token.paymentData.header.transactionId,
                version: event.payment.token.paymentData.version,
                transactionIdentifier: event.payment.token.transactionIdentifier,
                displayName: event.payment.token.paymentMethod.displayName,
                type: event.payment.token.paymentMethod.type,
                network: event.payment.token.paymentMethod.network,
                ephemeralPublicKey: event.payment.token.paymentData.header.ephemeralPublicKey,
            }
    
            const request: MakePaymentRequest = {
                user_id: userState.state.userId!!,
                table_id: checkOutState.state.tableId,
                check_please_payment_id: checkState.state.paymentId!!,
                payment_method: null,
                payment_digital_wallet_type: PaymentDigitalWalletType.ApplePay,
                payment_detail: paymentDetail,
                globalpay_hpp_response: null,
                apple_pay_token: applePayToken,
                google_pay_token: null
            };

            let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/checks/" + checkState.state.checkSummary!!.id + "/payment/process"

            const headers = {
                'X-API-Key': checkOutState.state.restaurantApiKey,
                'location_id': checkOutState.state.restaurantId
            }

            axios.post(url, request, { headers, cancelToken: source.token })
            .then(function(response) {
                let r = response.data as MakePaymentResponse;
                
                if (r.success === true) {
                    session.completePayment((window as any).ApplePaySession.STATUS_SUCCESS);

                    const state: CheckState = checkState.state;
                    state.checkSummary = null;
                    state.checkDetail = null;
                    state.checkTotal = null;
                    state.paymentId = null;
                    state.splitType = SplitType.None;
                    state.splitPercentage = 0;
                    state.splitAmount = 0;
                    state.splitEvenlyCount = 0;
                    state.checkTotalWithTip = '';
                    state.tip = 0;
                    state.tipString = '';
                    checkState.setState(state);

                    if (r.restaurant_id != null) {
                        const coState: CheckOutState = checkOutState.state;
                        coState.restaurantId = r.restaurant_id;
                        checkOutState.setState(coState);
                    }
        
                    history.push({
                        pathname: '/postcheckout/review',
                        state: { 
                            paymentId: r.check_please_payment_id,
                        }
                    });
                }
                else {
                    session.completePayment((window as any).ApplePaySession.STATUS_FAILURE);

                    if (r.error_code === ErrorCode.CheckAlreadyPaid) {
                        setCheckPaidError(true);
                        setErrorDialogTryAgain(false);
                        setErrorMessage(String_Check_CheckPaid.find(s => s.key === userState.state.locale)!!.value);
                        setErrorDialogOpen(true);
                    }            
                    else if (r.error_code === ErrorCode.PaymentFailed)
                    {
                        setErrorMessage(String_PayCheck_ErrorPaymentFailed.find(s => s.key === userState.state.locale)!!.value);
                        setErrorDialogTryAgain(false);
                        setErrorDialogOpen(true);
                    }
                    else if (r.error_code === ErrorCode.PaymentAmountExceedsAmountDue)
                    {
                        setPaymentExceedsAmountDueError(true);
                        setErrorMessage(String_PayCheck_PaymentExceedsAmountDue.find(s => s.key === userState.state.locale)!!.value);
                        setErrorDialogTryAgain(false);
                        setErrorDialogOpen(true);
                    }
                }              
            })
            .catch(function(error) {
                session.completePayment((window as any).ApplePaySession.STATUS_FAILURE);
                
                if (error.response) {
                    setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogTryAgain(false);
                    setErrorDialogOpen(true);
                }
                else {
                    setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogTryAgain(false);
                    setErrorDialogOpen(true);
                }
            });
        }

        session.oncancel = function (event: any) {
            
        }

        session.begin();
    }

    const scriptLoaded = () => {
        setIsLoaded(true)
    }

    const scriptError = () => {

    }

    const onCreditButtonClick = () => {
        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();

        if (!isAuthorized) {
            authorize(source);
        }

        setIsCreditCardSelected(true);
        
        window.removeEventListener("message", messageHandler);
        window.addEventListener("message", messageHandler);
    }

    const getPaymentMethodArrayGoogle = (): google.payments.api.CardNetwork[] => {
        const paymentMethodArray: google.payments.api.CardNetwork[] = []

        if (paymentMethodAllowed != undefined) {
            let hasVisa = paymentMethodAllowed & PaymentMethodsAccepted.Visa;
            let hasMastercard = paymentMethodAllowed & PaymentMethodsAccepted.Mastercard;
            let hasAmex = paymentMethodAllowed & PaymentMethodsAccepted.Amex;
            let hasDiscover = paymentMethodAllowed & PaymentMethodsAccepted.Discover;
            let hasJCB = paymentMethodAllowed & PaymentMethodsAccepted.JCB;
            
            if (hasVisa) {
                paymentMethodArray.push("VISA");
            }

            if (hasMastercard) {
                paymentMethodArray.push("MASTERCARD");
            }

            if (hasAmex) {
                paymentMethodArray.push("AMEX");
            }

            if (hasDiscover) {
                paymentMethodArray.push("DISCOVER");
            }

            if (hasJCB) {
                paymentMethodArray.push("JCB");
            }
        }
        else {
            paymentMethodArray.push("VISA");
            paymentMethodArray.push("MASTERCARD");
            paymentMethodArray.push("AMEX");
            paymentMethodArray.push("DISCOVER");
        }

        return paymentMethodArray;
    }

    const getPaymentMethodArrayApple = (): string[] => {
        const paymentMethodArray: string[] = []

        if (paymentMethodAllowed != undefined) {
            let hasVisa = paymentMethodAllowed & PaymentMethodsAccepted.Visa;
            let hasMastercard = paymentMethodAllowed & PaymentMethodsAccepted.Mastercard;
            let hasAmex = paymentMethodAllowed & PaymentMethodsAccepted.Amex;
            let hasDiscover = paymentMethodAllowed & PaymentMethodsAccepted.Discover;
            let hasJCB = paymentMethodAllowed & PaymentMethodsAccepted.JCB;

            if (hasVisa) {
                paymentMethodArray.push("visa");
            }

            if (hasMastercard) {
                paymentMethodArray.push("masterCard");
            }

            if (hasAmex) {
                paymentMethodArray.push("amex");
            }

            if (hasDiscover) {
                paymentMethodArray.push("discover");
            }

            if (hasJCB) {
                paymentMethodArray.push("jcb");
            }
        }
        else {
            paymentMethodArray.push("visa");
            paymentMethodArray.push("masterCard");
            paymentMethodArray.push("amex");
            paymentMethodArray.push("discover");
        }

        return paymentMethodArray;
    }

    const messageHandler = (event: { data: any; }) => {
        try{
            var data = event.data;

            if (event.data.action !== "applePay") {
                if (data.startsWith('response')) {
                    console.log("Data  "+data);
                    var splt = data.split('=');
                    var json = JSON.parse(splt[1]);
    
                    parPayResponseHandler(json);
                } else if (data.startsWith('enablePlaceOrder')) {
                    var splt = data.split('=');
                    var json = splt[1];
                    var enableBtn = json == 'true' ? true: false;
    
                    validateHandler(enableBtn);
                } else {
                    try {
                        var data = JSON.parse(event.data);
                        if (data.action === "parPaySubmit") {
                            var frame = document.getElementById('frame_carddetails');
                            //@ts-ignore
                            frame!!.contentWindow.postMessage('aurus-token', "*");
                        }
                    }
                    catch {
    
                    }
                }
            }
        }
        catch (exception) {

        }
    }

    const appleMessageHandler = (event: { data: any; }) => {
        try{
            var data = event.data;

            if (event.data.action === "applePay") {
                onApplePayClickHandler();
            }
        }
        catch (exception) {

        }
    }

    function parPayResponseHandler(JSONdata: any) {
        try {
            var respCode = JSONdata.response_code;
            var respTxt = JSONdata.response_text;
            var maskedCardNum = JSONdata.masked_card_num;
            var cardType = JSONdata.card_type;
            var cardExpiry = JSONdata.card_expiry_date;
            var token = JSONdata.one_time_token;
            var cardHolderName = JSONdata.card_holder_name;
  
            if (Number(JSONdata.response_code) > 0) { 
                errorHandler()
                setIsError(false);
                setErrorMessage(JSONdata.response_code + ' - ' + JSONdata.response_text);
                setErrorDialogOpen(true);
            } 
            else {
                successHandler(token)
            }
        } 
        catch (e) {
            errorHandler()
            setIsError(false);
            setErrorMessage(String_PayCheck_ErrorPaymentFailed.find(s => s.key === userState.state.locale)!!.value );
            setErrorDialogOpen(true);
        }
    }

    React.useEffect(() => {
        if (!isLoaded) {
            const LoadApplePayExternalScript = () => {
                const externalScript = document.createElement("script");
                externalScript.onload = applePayScriptLoaded;
                externalScript.onerror = applePayScriptError;
                externalScript.id = "external";
                externalScript.async = true;
                externalScript.type = "text/javascript";
                document.body.appendChild(externalScript);
                externalScript.src = getApplePayScriptUrl()
            }

            LoadApplePayExternalScript()

            const LoadExternalScript = () => {
                const externalScript = document.createElement("script");
                externalScript.onload = scriptLoaded;
                externalScript.onerror = scriptError;
                externalScript.id = "external";
                externalScript.async = true;
                externalScript.type = "text/javascript";
                document.body.appendChild(externalScript);
                externalScript.src = `https://code.jquery.com/jquery-3.1.0.min.js`;
            }

            LoadExternalScript()

            window.removeEventListener("message", appleMessageHandler);
            window.addEventListener("message", appleMessageHandler);
        }  
               
    });

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorDialogOpen(false);
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: false,
        onCloseHandler: handleErrorDialogClose,
    }
    
    const authorize = (cancelTokenSource: CancelTokenSource) => {

        const request: CreatePaymentRequest = {
            total: total,
            user_id: userState.state.userId!!,
            table_id: checkOutState.state.tableId,
            check_please_payment_id: checkState.state.paymentId!!
        };

        let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/checks/" + checkState.state.checkSummary!!.id + "/payment/create"

        const headers = {
            'X-API-Key': checkOutState.state.restaurantApiKey,
            'location_id': checkOutState.state.restaurantId
        }

        axios.post(url, request, { headers, cancelToken: cancelTokenSource.token })
        .then(function(response) {
            let r = response.data as CreatePaymentResponse;

            if (r.success === true) {
                
                if (paymentMethodAllowed !== undefined) {
                    let hasVisa = paymentMethodAllowed & PaymentMethodsAccepted.Visa;
                    let hasMastercard = paymentMethodAllowed & PaymentMethodsAccepted.Mastercard;
                    let hasAmex = paymentMethodAllowed & PaymentMethodsAccepted.Amex;
                    let hasDiscover = paymentMethodAllowed & PaymentMethodsAccepted.Discover;
                    var paymentOptionsString = "";
                    if (hasVisa) {
                        paymentOptionsString += "VS,"
                    }
    
                    if (hasMastercard) {
                        paymentOptionsString += "MC,"
                    }
    
                    if (hasAmex) {
                        paymentOptionsString += "AX,"
                    }
    
                    if (hasDiscover) {
                        paymentOptionsString += "NS,"
                    }
    
                    paymentOptionsString = paymentOptionsString.substring(0, paymentOptionsString.length - 1);
                    setAcceptedPaymentTypes(paymentOptionsString);
                }
                
                setIsAuthorized(true);
                setIframeUrl(r.checkout_url);
            }
            else {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                setErrorDialogOpen(true);
            }
        })
        .catch(function(error) {
            if (error.response) {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
            }
            else {
                setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
            }

            setErrorDialogOpen(true);
        });
    };

    const mapCardType = (cardType: string): PaymentMethod | null => {
        let paymentMethod: PaymentMethod | null = null;

        switch(cardType.toLowerCase())
        {
            case 'visa':
                paymentMethod = PaymentMethod.Visa;
                break;
            case 'mastercard':
                paymentMethod = PaymentMethod.Mastercard;
                break;
            case 'amex':
                paymentMethod = PaymentMethod.Amex;
                break;
            case 'discover':
                paymentMethod = PaymentMethod.Discover;
                break;
        }

        return paymentMethod;
    }

    return (
        <div className={clsx({
            [classes.root]: isPortrait,
            [classes.rootLandscape]: !isPortrait,
        })}>

            {isLoaded && 
                <div className={classes.paymentTerms}>
                    <PaymentTerms />
                </div>                
            }        

            {isCreditCardSelected &&
                <div className={classes.fieldContainer}>
                    <iframe id="frame_carddetails" name="frame_carddetails" className={clsx({
                            [classes.iframe]: isPortrait,
                            [classes.iframeLandscape]: !isPortrait,
                    })}
                    src={iframeUrl} />
                </div>
            }

            {!isCreditCardSelected &&
                <div className={classes.creditButton} id="btnCredit" onClick={onCreditButtonClick}>Credit Card</div>
            }    

            {!isCreditCardSelected && ((checkOutState.state.restaurantGooglePayMerchantId !== null) || (checkOutState.state.restaurantGooglePayMerchantId !== null)) &&
                <div>
                    {(checkOutState.state.applePayMerchantId !== null) &&
                        <div ref={applePayEl}>

                        </div> 
                    }
                    {(checkOutState.state.restaurantGooglePayMerchantId !== null) && 
                        <GooglePayButton
                            className={classes.googlePayButton}
                            environment={process.env.REACT_APP_GOOGLEPAY_ENVIRONMENT!! as google.payments.api.Environment}
                            buttonColor="default"
                            buttonType="checkout"
                            buttonSizeMode="fill"
                            onLoadPaymentData={paymentRequest => {
                                googlePaySuccessHandler(paymentRequest)
                            }}
                            onError={error => {
                                console.log(error);
                                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                                setErrorDialogTryAgain(true);
                                setErrorDialogOpen(true);
                            }}
                            paymentRequest={{
                                apiVersion: 2,
                                apiVersionMinor: 0,
                                allowedPaymentMethods: [
                                {
                                    type: 'CARD',
                                    parameters: {
                                        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                                        allowedCardNetworks: getPaymentMethodArrayGoogle(),
                                    },
                                    tokenizationSpecification: {
                                        type: 'PAYMENT_GATEWAY',
                                        parameters: {
                                            gateway: 'auruspay',
                                            gatewayMerchantId: '22731101231178566',
                                        },
                                    },
                                },
                                ],
                                merchantInfo: {
                                    merchantId: checkOutState.state.restaurantGooglePayMerchantId!!,
                                    merchantName: checkOutState.state.restaurantGooglePayMerchantName!!,
                                },
                                transactionInfo: {
                                    totalPriceStatus: 'FINAL',
                                    totalPriceLabel: 'Total',
                                    totalPrice: (total / 100).toString(),
                                    currencyCode: checkOutState.state.restaurantCurrency!!,
                                    countryCode: checkOutState.state.restaurantCountry!!,
                                },
                            }}
                        />
                        }
                </div>
            }

            <ErrorDialog
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />
        </div>
        
    );
}